import { Draggable } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { clsx } from '~/styles/utils';

import { useFormWithLinesContext } from '../FormLines.context';
import { useSelectedContactLanguage } from '../FormLines.hooks';
import { useSectionContext } from '../Section/Section.context';
import { useLineContext } from '../SectionLines/SectionLines.context';
import { FormActivityType } from './FormActivityType/FormActivityType';
import { FormDescription } from './FormDescription/FormDescription';
import { FormDiscount } from './FormDiscount/FormDiscount';
import { FormPrice } from './FormPrice/FormPrice';
import { FormQuantity } from './FormQuantity/FormQuantity';
import { useIsFreeLine, useLineProduct, useLineTotal, useProductName } from './Line.hooks';
import { LineActions } from './LineActions/LineActions';
import {
  cardWrapperStyle,
  creativeWorkStyle,
  detailsStyle,
  detailsWrapperStyle,
  discountedStyle,
  emphasisStyle,
  productIconStyle,
  rowStyle,
  summaryActionsButtonStyle,
  summaryActionsStyle,
  summaryDescriptionStyle,
  summaryStyle,
  summaryTotalAmountStyle,
  summaryTotalStyle,
} from './LineMobile.css';
import { ProductName } from './ProductName/ProductName';

export const LineMobile = () => {
  const {
    formState: { errors },
  } = useFormWithLinesContext();

  const {
    mobileLineReducer: [expandedRow],
    section,
    sectionIndex,
  } = useSectionContext();
  const { line, lineIndex } = useLineContext();

  const isExpanded = expandedRow === `${sectionIndex}-${lineIndex}`;
  const lineErrors = errors.sections?.[sectionIndex]?.lines?.[lineIndex];

  return (
    <Draggable draggableId={`${line.uuid}`} index={lineIndex}>
      {(provided, snapshot) => (
        <tr
          className={rowStyle}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          data-dragging={snapshot.isDragging || undefined}
          data-expanded={isExpanded || undefined}
          data-indented={section.isPhotoAlbum || undefined}
          data-invalid={!!lineErrors || undefined}
          data-line-type={line.type}
          data-testid="line"
        >
          {line.type === 'entry' ? <EntryLineMobile /> : <TextLineMobile />}
        </tr>
      )}
    </Draggable>
  );
};

const EntryLineMobile = () => {
  const { watch } = useFormWithLinesContext();
  const {
    mobileLineReducer: [, setExpandedLine],
    sectionIndex,
  } = useSectionContext();
  const { isProductLine, lineFieldName, lineIndex } = useLineContext();

  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['common', 'lines', 'validation']);

  const watchDescription = watch(`${lineFieldName}.description`) ?? '';
  const watchDiscountType = watch(`${lineFieldName}.discountType`);
  const watchActivityType = watch(`${lineFieldName}.activityType`);

  const product = useLineProduct(lineFieldName);
  const contactLanguage = useSelectedContactLanguage();
  const productName = useProductName(product, contactLanguage);
  const isFreeLine = useIsFreeLine(lineFieldName);
  const { discountedTotal, total } = useLineTotal(lineFieldName);

  return (
    <>
      <td className={cardWrapperStyle} onClick={() => setExpandedLine({ sectionIndex, lineIndex })}>
        <div className={summaryStyle} data-product={isProductLine || undefined}>
          {isProductLine && (
            <span className={productIconStyle}>
              <Tooltip content={t('lines:productName.tooltip')}>
                <Icon name="Package" />
              </Tooltip>
            </span>
          )}

          <p className={summaryDescriptionStyle}>
            <strong>{product !== null ? productName : watchDescription || t('lines:description.placeholder')}</strong>
            {isProductLine && watchDescription && <em className={emphasisStyle}>{watchDescription}</em>}
          </p>

          <p className={creativeWorkStyle}>{t(`lines:creativeWork.options.${watchActivityType}.label`)}</p>

          <p className={summaryTotalStyle}>
            <span className={clsx(summaryTotalAmountStyle, watchDiscountType !== null && discountedStyle)}>{formatCurrency(total)}</span>
            {watchDiscountType !== null && (
              <span className={summaryTotalAmountStyle}>{isFreeLine ? t('lines:discount.free') : formatCurrency(discountedTotal)}</span>
            )}
          </p>

          <div className={summaryActionsStyle}>
            <LineActions className={summaryActionsButtonStyle} />
          </div>
        </div>
      </td>

      <td className={cardWrapperStyle}>
        <div className={detailsWrapperStyle}>
          <div className={detailsStyle}>
            {product !== null && <ProductName value={productName} />}
            <FormDescription />
            <FormQuantity />
            <FormPrice />
            <FormDiscount />
            <FormActivityType />
          </div>
        </div>
      </td>
    </>
  );
};

const TextLineMobile = () => {
  const { watch } = useFormWithLinesContext();
  const {
    mobileLineReducer: [, setExpandedLine],
    sectionIndex,
  } = useSectionContext();
  const { lineFieldName, lineIndex } = useLineContext();

  const { t } = useTranslation(['common', 'lines', 'validation']);

  const watchDescription = watch(`${lineFieldName}.description`) ?? '';

  return (
    <>
      <td className={cardWrapperStyle} onClick={() => setExpandedLine({ sectionIndex, lineIndex })}>
        <div className={summaryStyle}>
          <p className={summaryDescriptionStyle}>
            <strong>{watchDescription || t('lines:textLine')}</strong>
          </p>
          <div className={summaryActionsStyle}>
            <LineActions className={summaryActionsButtonStyle} />
          </div>
        </div>
      </td>

      <td className={cardWrapperStyle}>
        <div className={detailsWrapperStyle}>
          <div className={detailsStyle}>
            <FormDescription />
          </div>
        </div>
      </td>
    </>
  );
};
